import { createPortal } from "react-dom";
import { useTheme } from "../../../context/ThemeProvider";
import { WarningI, WarningIDark } from "../../../assets";
import { ThirdButton } from "../../Button/Buttons";
import { useDeleteSpectraMutation, useLeaveSpectraMutation, useSpectraSparks } from "../../../api/queryHooks";
import UserAuthStore from "../../../store/userStore";
import { useNavigate } from "react-router-dom";
import useMoveSparksModalStore from "../../../store/useMoveSparksModal";
import { useEffect } from "react";
import ResponseLoader from "../../Loader/ResponseLoader";
import useSessionIdStore from "../../../store/useSessionIdStore";
import useSpectraDeleteModalStore from "../../../store/useSpectraDeleteModal";
import { PERMISSIONS } from "../../../utils/permissions";

const Modal = ({ onClose, onSuccessCallback, space }) => {
  const { theme } = useTheme();
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const isOwner = PERMISSIONS.IS_OWNER(space);
  const toggleMoveSparkModal = useMoveSparksModalStore(
    (state) => state.setIsOpen
  );
  const canLoadSparks = !space.status || space.status === "READY";
  
  const { data: sparksData, isLoading: isLoadingSparks } = useSpectraSparks(
    space.space_id,
    orgId,
    user?.uid,
    space.owner_id,
    { enabled: canLoadSparks && !!user?.uid && !!orgId && !!space.space_id }
  );
  const sparks = sparksData?.sparks || [];
  
  const { mutate: deleteSpectra, isPending: isDeleting } = useDeleteSpectraMutation();

  const { mutate: leaveSpectra, isPending: isLeaving } = useLeaveSpectraMutation({
    onSuccess: () => {
      onSuccessCallback();
    },
  });

  const handleActionClick = () => {
    const payload = {
      sid: space.space_id,
      org_id: orgId,
      uid: user.uid,
      session_id: useSessionIdStore.getState().session_id,
    };

    // Close modal immediately — optimistic update handles the UI
    onSuccessCallback();

    if (isOwner) {
      deleteSpectra(payload);
    } else {
      leaveSpectra(payload);
    }
  };

  const handleMoveSparksClick = () => {
    toggleMoveSparkModal(true, space);
    onClose();
  };

  useEffect(() => {
    // React Query handles spectra sparks fetching via useSpectraSparks hook
    // No manual fetching needed
  }, [orgId, user?.uid, space.space_id, space.owner_id, isOwner]);

  const isLoading = isDeleting || isLeaving;

  return (
    <div
      className="relative max-w-[500px] w-full bg-surface-dialog py-6 shadow-[0px_1px_4px_#00000029] rounded-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-center">
        {theme === "dark" ? <WarningIDark /> : <WarningI />}
      </div>
      <div className="text-ink-ink dark:text-ink-white font-semibold text-sm text-center mt-7">
        {isOwner
          ? "Are you sure you want to delete this spectra?"
          : "Are you sure you want to leave this spectra?"}
      </div>
      <div className="px-6 mt-4 flex justify-center items-center">
        <span className="text-ink-ink dark:text-ink-white text-[13px] text-center max-w-[280px]">
          {isOwner
            ? "All sparks and files will be delete permanently. You can move them out of this spectra if you wish to store them."
            : "You will lose access to this spectra and all its sparks. Your created sparks will remain accessible to other members."}
        </span>
      </div>
      <hr className="my-5 dark:border-edge-hex-aaaaaa80" />
      <div className="px-3 xm:px-6 flex flex-wrap gap-3 text-nowrap items-center justify-between flex-col xm:flex-row">
        <div>
          {isOwner && (isLoadingSparks ? (
            <ThirdButton className="min-w-24 flex justify-center items-center">
              <ResponseLoader />
            </ThirdButton>
          ) : sparks?.length ? (
            <ThirdButton
              onClick={handleMoveSparksClick}
              className="bg-button-accent border-button-border text-button-on-primary border-[0.5px] dark:border-0"
            >
              Move Sparks
            </ThirdButton>
          ) : null)}
        </div>
        <div className="flex items-center gap-3">
          <ThirdButton
            onClick={onClose}
            className="bg-button-neutral-surface border-button-border text-button-neutral-text border-[0.5px] dark:border-0"
          >
            Cancel
          </ThirdButton>
          <ThirdButton
            onClick={handleActionClick}
            className="bg-button-danger-solid border-button-border text-button-on-primary border-[0.5px] dark:border-0"
          >
            {isLoading ? (isOwner ? "Deleting..." : "Leaving...") : (isOwner ? "Delete" : "Leave")}
          </ThirdButton>
        </div>
      </div>
    </div>
  );
};

const SpectraDeleteModal = () => {
  const isOpen = useSpectraDeleteModalStore((state) => state.isOpen);
  const spaceToDelete = useSpectraDeleteModalStore(
    (state) => state.spaceToDelete
  );
  const setIsOpen = useSpectraDeleteModalStore((state) => state.setIsOpen);
  const callback = useSpectraDeleteModalStore((state) => state.callback);
  const navigate = useNavigate();

  const handleOutsideClick = (e) => {
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const onSuccess = () => {
    callback?.();
    handleClose();
    navigate(`/spectra`);
  };

  if (isOpen && spaceToDelete)
    return createPortal(
      <div
        className="absolute z-[1000] top-0 left-0 w-screen px-5 h-[100dvh] bg-fill-black/50 flex items-center justify-center"
        onClick={handleOutsideClick}
      >
        <Modal
          onClose={handleClose}
          onSuccessCallback={onSuccess}
          space={spaceToDelete}
        />
      </div>,
      document.body
    );
};

export default SpectraDeleteModal;
